/**
 * 後台 — 出貨管理(全部歷史出貨紀錄 + 勾選匯出 Excel)
 *
 * 兩個來源合併(後端 list_all_shipments):
 *   訂單分批出貨(source=order,可點回訂單詳情)
 *   存摺直接出貨(source=wallet,/admin/ship 出的)
 * 搜尋(姓名/手機/單號/訂單編號)+ 出貨日期區間 in-memory filter;
 * 勾選(或全選)→ 匯出 .xlsx(一列一品項,方便樞紐統計)。
 *
 * Route: /admin/shipments
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import {
  adminListAllShipments,
  adminExportShipments,
  CARRIER_LABELS,
  type ShipmentRecord,
} from "../../api/admin";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpeCompact } from "../../utils/date";


export default function ShipmentList() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [rows, setRows] = useState<ShipmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await adminListAllShipments();
        setRows(r.shipments);
      } catch {
        showToast("出貨紀錄載入失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase().replace(/^#/, "");
    return rows.filter((r) => {
      if (dateFrom && (r.ship_date || "") < dateFrom) return false;
      if (dateTo && (r.ship_date || "") > dateTo) return false;
      if (!s) return true;
      return (
        (r.member_name || "").toLowerCase().includes(s) ||
        (r.phone || "").includes(s) ||
        (r.number || "").toLowerCase().includes(s) ||
        (r.order_id || "").toLowerCase().includes(s)
      );
    });
  }, [rows, search, dateFrom, dateTo]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.key));

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((r) => next.delete(r.key));
      } else {
        filtered.forEach((r) => next.add(r.key));
      }
      return next;
    });
  }

  function toggleOne(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function exportExcel() {
    if (selected.size === 0) {
      showToast("請先勾選要匯出的出貨紀錄", "error");
      return;
    }
    setExporting(true);
    try {
      const blob = await adminExportShipments(Array.from(selected));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `出貨紀錄_${todayTpeCompact()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      showToast(`已匯出 ${selected.size} 筆出貨紀錄`, "success");
    } catch {
      showToast("匯出失敗,請再試一次", "error");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="shipments" />

      <div className="px-6 mt-6 space-y-4">
        {/* 篩選列 + 匯出 */}
        <div className="card">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-48">
              <label className="label">搜尋</label>
              <input
                className="input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="姓名 / 手機 / 追蹤單號 / 訂單編號"
              />
            </div>
            <div>
              <label className="label">出貨日 起</label>
              <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">出貨日 迄</label>
              <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <button
              onClick={exportExcel}
              disabled={exporting || selected.size === 0}
              className="btn-primary disabled:opacity-50"
              title={selected.size === 0 ? "勾選後匯出" : ""}
            >
              {exporting ? "匯出中…" : `匯出 Excel(${selected.size})`}
            </button>
          </div>
          <div className="text-xs text-muted mt-2">
            共 {filtered.length} 筆出貨紀錄{selected.size > 0 ? ` · 已勾選 ${selected.size} 筆` : ""}
          </div>
        </div>

        {/* 列表 */}
        <div className="card p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-muted text-sm">載入中…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted text-sm">沒有符合條件的出貨紀錄</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted text-xs border-b border-line bg-cream/50">
                  <th className="p-3 w-10">
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="w-4 h-4" />
                  </th>
                  <th className="text-left p-3">出貨日</th>
                  <th className="text-left p-3">會員</th>
                  <th className="text-left p-3">品項</th>
                  <th className="text-left p-3">物流</th>
                  <th className="text-left p-3">來源</th>
                  <th className="text-left p-3">經手人</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.key} className="border-b border-line/50 hover:bg-cream/30">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(r.key)}
                        onChange={() => toggleOne(r.key)}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="p-3 whitespace-nowrap">{r.ship_date || "—"}</td>
                    <td className="p-3">
                      <div>{r.member_name || "—"}</div>
                      <div className="text-xs text-muted">{r.phone || ""}</div>
                    </td>
                    <td className="p-3">
                      {r.lines.map((ln, i) => (
                        <div key={i} className="whitespace-nowrap">
                          {ln.name} <span className="text-muted">× {ln.qty} {ln.unit}</span>
                        </div>
                      ))}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <div>{CARRIER_LABELS[r.carrier] || r.carrier || "—"}</div>
                      {r.number && <div className="text-xs text-muted font-mono">{r.number}</div>}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {r.source === "order" ? (
                        <button
                          onClick={() => navigate(`/admin/orders/${r.order_id}`)}
                          className="text-primary hover:underline"
                        >
                          訂單 #{r.order_id.slice(0, 8)}
                        </button>
                      ) : (
                        <span className="text-muted">存摺出貨</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted">{r.operator || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
